/**
 * The practice-forms schema logic — `tasks/patient-forms.md` §13.1 / §13.4.
 *
 * This is the CLIENT half of a deliberately duplicated pair. The SERVER half is
 * `utils/customFormSchema.js`, and `scripts/verify-custom-form-validation.js` runs both over
 * `scripts/fixtures/custom-form-cases.json` and asserts, case by case, that they return the same
 * thing — because a renderer that hides a question the server then demands, or a screen that shows
 * the patient a different score from the one filed in their chart, are silent failures that only
 * surface in a patient record.
 *
 * ── NO ANGULAR IMPORTS, AND NOTHING BUT ITS OWN TYPES ────────────────────────────────────────
 *
 * That is not tidiness: it is what lets the verification harness compile this file with plain
 * `tsc` and `require()` the emitted JavaScript from node, so the two implementations can be run
 * against the same bytes. An `@angular/core` import here would make the cross-language check
 * impossible to run and the drift it catches invisible again. Keep this file free of DI,
 * `HttpClient`, RxJS and anything that touches the DOM.
 *
 * ── EXPORTS ──────────────────────────────────────────────────────────────────────────────────
 *
 *   validateSchema(schema)            -> SchemaValidationResult
 *   visibleQuestions(schema, answers) -> FormQuestion[]      (document order, showIf applied)
 *   evaluateShowIf(showIf, answers)   -> boolean
 *   validateAnswers(schema, answers)  -> AnswerValidationResult
 *   scoreAnswers(schema, answers)     -> FormScore | null
 *
 * Same five names, same semantics, same edge cases, same error STRINGS as the server file. If you
 * change one, change both and re-run `npm run verify:custom-form-validation`.
 *
 * ── THE ONE BEHAVIOUR EVERYTHING ELSE SERVES ─────────────────────────────────────────────────
 *
 * `validateAnswers` ONLY EVER CONSIDERS VISIBLE QUESTIONS. A required question behind a `showIf`
 * that is false must not block submission — otherwise a patient who answered "no" to "are you
 * pregnant?" can never submit, because the follow-ups they were never shown are required and
 * unanswerable. There is no support path out of that; the patient simply gives up.
 */

import {
  AnswerError,
  AnswerValidationResult,
  FormAnswers,
  FormOption,
  FormQuestion,
  FormSchema,
  FormScore,
  FormSection,
  SchemaValidationResult,
  ShowIf,
} from '../models/custom-form.model';

/** §13.1's `FormQuestionType`, exactly. Anything else is a schema a renderer cannot draw. */
export const QUESTION_TYPES: string[] = [
  'text', 'textarea', 'number', 'date',
  'radio', 'checkbox', 'select', 'yesno',
  'scale', 'signature', 'statictext',
];

/** The three types whose answers are constrained to a fixed vocabulary. */
export const OPTION_TYPES: string[] = ['radio', 'checkbox', 'select'];

/** The two types that carry a numeric range. */
const RANGE_TYPES: string[] = ['number', 'scale'];

/** §13.1's `ShowIf.operator`. */
export const SHOW_IF_OPERATORS: string[] = ['eq', 'ne', 'in', 'gt', 'lt', 'answered'];

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Small shared predicates. Every one is mirrored in `utils/customFormSchema.js`; they are the
 * places where the two implementations would otherwise drift apart.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

function isObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isNonEmptyString(value: any): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

/**
 * LOOSE SCALAR EQUALITY — and the bug this exists to prevent.
 *
 * A form control hands back the STRING `'3'` for an option whose schema value is the NUMBER `3`
 * (or the reverse, once a schema has been through `JSON.parse` on a value a builder typed into a
 * number input). Under `===` the two disagree, so:
 *
 *   · a `showIf` keyed on that option never fires, and the follow-up question never appears; and
 *   · `validateAnswers` rejects the patient's answer as "not one of the options offered", on a
 *     value the renderer itself produced.
 *
 * Both are invisible in a builder preview, where the same in-memory object is used on both sides.
 * So comparison is by STRING FORM: `3` and `'3'` agree, and `true` and `'true'` agree (which is
 * what a `yesno` question needs when a builder types `true` into the showIf value box).
 *
 * The known limit, stated rather than hidden: `3.0` and `'3.0'` do NOT agree, because
 * `String(3.0) === '3'`. Option values are authored as strings in the builder, so this only bites
 * a hand-written schema, and it bites both implementations identically.
 */
function looseEq(a: any, b: any): boolean {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (typeof a === 'object' || typeof b === 'object') return false; // arrays handled by callers
  return String(a) === String(b);
}

/**
 * A number, or null. `''`, whitespace, `null`, `undefined`, booleans, arrays and objects are all
 * NOT numbers — note in particular that `Number('')` and `Number(null)` are both `0` in
 * JavaScript, so the naive `Number(x)` would read an unanswered question as the number zero and
 * make `gt: -1` true on a blank field.
 */
function toNumberOrNull(value: any): number | null {
  if (typeof value === 'number') return isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Is this answer present?
 *
 * Present means: not `undefined`, not `null`, not `''` (nor whitespace only — a required text
 * question answered with a space has not been answered), and not `[]`.
 *
 * `false` IS answered and `0` IS answered. A patient who ticked "No" on a `yesno` and a patient
 * who entered `0` on a pain scale have both answered; treating either as absent would make a
 * required question unsubmittable for exactly the patients giving the negative answer.
 */
function isAnswered(value: any): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/** A finite numeric `score` on an option, or null. `score` is optional throughout §13.1. */
function optionScore(option: any): number | null {
  if (!isObject(option)) return null;
  return typeof option.score === 'number' && isFinite(option.score) ? option.score : null;
}

interface FlatEntry {
  section: FormSection;
  question: FormQuestion;
}

/**
 * Every question in DOCUMENT ORDER, tagged with the section it came from.
 *
 * Document order is: sections in array order, questions in array order within each section. It is
 * the order the patient sees, and therefore the only order in which "earlier" means anything —
 * both `showIf` back-references and the chained-visibility rule depend on it.
 *
 * Tolerates a malformed schema by skipping, so `visibleQuestions` and `scoreAnswers` never throw
 * on data `validateSchema` would reject.
 */
function flatten(schema: any): FlatEntry[] {
  const out: FlatEntry[] = [];
  if (!isObject(schema) || !Array.isArray(schema.sections)) return out;
  for (const section of schema.sections) {
    if (!isObject(section) || !Array.isArray(section.questions)) continue;
    for (const question of section.questions) {
      if (!isObject(question)) continue;
      out.push({ section, question });
    }
  }
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * evaluateShowIf
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Evaluate ONE `showIf` against a flat answers map. No boolean algebra — §13.1 is one level only.
 *
 * A missing / null / non-object `showIf` returns `true`: a question with no condition is shown.
 *
 * Judgement calls made here, all mirrored on the server:
 *
 *  · `eq`/`ne` against an ARRAY answer (a `checkbox`) test MEMBERSHIP — "show if the patient
 *    ticked X". A checkbox answer is never a scalar, so the alternative is that `eq` can never be
 *    true against one, which makes the commonest conditional in a medical form ("if you ticked
 *    'diabetes', tell us more") impossible to express.
 *  · `ne` is the exact negation of `eq`, INCLUDING when the controlling question is unanswered:
 *    `ne: 'yes'` on a blank question is TRUE, so the dependent question shows. Showing a question
 *    is recoverable; hiding a required one is the failure this module exists to prevent.
 *  · `gt`/`lt` are numeric and return FALSE for a non-numeric answer or a non-numeric comparison
 *    value, rather than throwing or coercing a blank to zero.
 *  · an unrecognised operator returns FALSE. `validateSchema` rejects such a schema at publish
 *    time, so this only ever applies to data that got in another way.
 */
export function evaluateShowIf(showIf: ShowIf | undefined | null, answers: FormAnswers): boolean {
  if (!isObject(showIf)) return true;

  const condition = showIf as ShowIf;
  const map: FormAnswers = isObject(answers) ? answers : {};
  const answer = map[condition.questionId];
  const operator: string = condition.operator as string;
  const value = condition.value;

  if (operator === 'answered') return isAnswered(answer);

  if (operator === 'eq' || operator === 'ne') {
    const matched = Array.isArray(answer)
      ? answer.some((member: any) => looseEq(member, value))
      : looseEq(answer, value);
    return operator === 'eq' ? matched : !matched;
  }

  if (operator === 'in') {
    const list: any[] = Array.isArray(value) ? value : [value];
    if (Array.isArray(answer)) {
      return answer.some((member: any) => list.some((candidate) => looseEq(member, candidate)));
    }
    return list.some((candidate) => looseEq(answer, candidate));
  }

  if (operator === 'gt' || operator === 'lt') {
    const left = toNumberOrNull(answer);
    const right = toNumberOrNull(value);
    if (left === null || right === null) return false;
    return operator === 'gt' ? left > right : left < right;
  }

  return false;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * visibleQuestions
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * The visible questions with their sections, in document order.
 *
 * CHAINED VISIBILITY. A question whose controlling question is itself hidden is ALSO hidden, even
 * when the answers map still holds an answer for that hidden controller — which it routinely does,
 * because a patient answers Q2, Q3 appears, then they change Q1 and Q2 disappears with its answer
 * still in the autosaved draft. Evaluating each `showIf` in isolation would leave Q3 on screen,
 * gated by an answer to a question no longer being asked.
 *
 * So visibility is computed in ONE PASS in document order, carrying each question's decision
 * forward. `showIf.questionId` is required by §13.1 to name an EARLIER question, and
 * `validateSchema` enforces that, which is exactly what makes a single forward pass sufficient.
 *
 * If the reference names a question that is unknown or not yet seen (a schema `validateSchema`
 * would have rejected), there is no carried decision to apply, and the condition is evaluated
 * against the answers alone — the same thing a naive renderer would do.
 */
function visibleEntries(schema: any, answers: FormAnswers): FlatEntry[] {
  const map: FormAnswers = isObject(answers) ? answers : {};
  const entries = flatten(schema);
  const visibleById: { [id: string]: boolean } = Object.create(null);
  const out: FlatEntry[] = [];

  for (const entry of entries) {
    const question: any = entry.question;
    let visible: boolean;

    if (!isObject(question.showIf)) {
      visible = true;
    } else {
      const controllerId = question.showIf.questionId;
      const controllerSeen = Object.prototype.hasOwnProperty.call(visibleById, controllerId);
      if (controllerSeen && visibleById[controllerId] === false) {
        visible = false; // the chained rule: a hidden controller hides its dependants
      } else {
        visible = evaluateShowIf(question.showIf, map);
      }
    }

    if (isNonEmptyString(question.id)) visibleById[question.id] = visible;
    if (visible) out.push(entry);
  }

  return out;
}

export function visibleQuestions(schema: FormSchema, answers: FormAnswers): FormQuestion[] {
  return visibleEntries(schema, answers).map((entry) => entry.question);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * validateSchema
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Reject a schema that would break a renderer or a submission LATER.
 *
 * This runs at publish time (§13.2 `POST /custom-forms/:id/versions`) and in the builder, which is
 * the last moment a problem is cheap. A `CustomFormVersion` is immutable once written and patients
 * get sent links to it; a broken one is discovered by a patient who cannot submit, days later.
 *
 * The errors are written for a PRACTICE USER building a form, not for a developer reading a stack
 * trace. They name the question and say what to do.
 *
 * Errors come back in DOCUMENT ORDER so the builder can walk the list top to bottom, and the
 * strings are identical to the server's, so the cross-language check compares them literally.
 */
export function validateSchema(schema: any): SchemaValidationResult {
  const errors: string[] = [];

  if (!isObject(schema)) {
    return { valid: false, errors: ['The form has no content.'] };
  }

  if (schema.schemaVersion !== 1) {
    errors.push('The form schema version must be 1.');
  }

  if (!Array.isArray(schema.sections) || schema.sections.length === 0) {
    errors.push('The form has no sections. Add at least one section with at least one question.');
    return { valid: false, errors };
  }

  // Pass 1 — the ordered list of question ids, so a bad `showIf` can be told apart from a FORWARD
  // `showIf`. Both are fatal, but they are different mistakes and get different advice.
  const orderedIds: (string | null)[] = [];
  for (const entry of flatten(schema)) {
    const id: any = (entry.question as any).id;
    orderedIds.push(isNonEmptyString(id) ? id : null);
  }

  // Pass 2 — everything else, in document order.
  const seenSectionIds: { [id: string]: boolean } = Object.create(null);
  const seenQuestionIds: { [id: string]: boolean } = Object.create(null);
  let questionIndex = -1;

  for (let s = 0; s < schema.sections.length; s++) {
    const section: any = schema.sections[s];
    const sectionLabel = `Section ${s + 1}`;

    if (!isObject(section)) {
      errors.push(`${sectionLabel} is empty and cannot be saved.`);
      continue;
    }

    if (!isNonEmptyString(section.id)) {
      errors.push(`${sectionLabel} has no id.`);
    } else if (seenSectionIds[section.id]) {
      errors.push(`Section id "${section.id}" is used more than once.`);
    } else {
      seenSectionIds[section.id] = true;
    }

    const sectionName = isNonEmptyString(section.title) ? section.title : sectionLabel;

    if (!Array.isArray(section.questions) || section.questions.length === 0) {
      errors.push(`Section "${sectionName}" has no questions. Add a question or remove the section.`);
      continue;
    }

    for (let q = 0; q < section.questions.length; q++) {
      const question: any = section.questions[q];

      if (!isObject(question)) {
        errors.push(`Question ${q + 1} in section "${sectionName}" is empty and cannot be saved.`);
        continue;
      }

      questionIndex++;
      const id: string | null = isNonEmptyString(question.id) ? question.id : null;
      const label = id ? `Question "${id}"` : `Question ${q + 1} in section "${sectionName}"`;

      if (!id) {
        errors.push(`Question ${q + 1} in section "${sectionName}" has no id.`);
      } else if (seenQuestionIds[id]) {
        // Answers are a flat map keyed on question id (§13.1). Two questions sharing an id do not
        // merely look odd — the second silently OVERWRITES the first's answer, so one of the two
        // answers the patient gave is simply lost, with no error anywhere.
        errors.push(`Question id "${id}" is used more than once. Answers are stored by question id, so the second question would overwrite the first.`);
      } else {
        seenQuestionIds[id] = true;
      }

      if (!isNonEmptyString(question.question)) {
        errors.push(`${label} has no question text.`);
      }

      if (QUESTION_TYPES.indexOf(question.type) === -1) {
        errors.push(`${label} has an unknown type "${String(question.type)}".`);
      } else {
        if (OPTION_TYPES.indexOf(question.type) !== -1) {
          const options: any[] = Array.isArray(question.options) ? question.options : [];
          if (options.length < 1) {
            errors.push(`${label} is a ${question.type} question but has no options to choose from.`);
          } else {
            const seenValues: { [value: string]: boolean } = Object.create(null);
            for (const option of options) {
              const value = isObject(option) ? option.value : undefined;
              if (value === undefined || value === null || String(value) === '') {
                errors.push(`${label} has an option with no value.`);
                continue;
              }
              const key = String(value);
              if (seenValues[key]) {
                errors.push(`${label} has more than one option with the value "${key}".`);
              } else {
                seenValues[key] = true;
              }
            }
          }
        }

        if (RANGE_TYPES.indexOf(question.type) !== -1) {
          const min = toNumberOrNull(question.min);
          const max = toNumberOrNull(question.max);
          if (min !== null && max !== null && min > max) {
            errors.push(`${label} has a minimum (${min}) that is greater than its maximum (${max}).`);
          }
        }

        if (question.type === 'statictext' && question.required === true) {
          // Display-only text has no control, so nothing can ever be entered for it. A form
          // carrying one is submittable by nobody.
          errors.push(`${label} is display-only text, so it cannot be marked as required.`);
        }
      }

      if (question.showIf !== undefined && question.showIf !== null) {
        if (!isObject(question.showIf)) {
          errors.push(`${label} has a display condition that is not usable.`);
        } else {
          const refId = question.showIf.questionId;
          const operator = question.showIf.operator;

          if (!isNonEmptyString(refId)) {
            errors.push(`${label} has a display condition that does not name a question.`);
          } else if (id && refId === id) {
            errors.push(`${label} is shown based on its own answer, so it can never appear.`);
          } else {
            const refIndex = orderedIds.indexOf(refId);
            if (refIndex === -1) {
              errors.push(`${label} is shown based on "${refId}", which is not a question on this form.`);
            } else if (refIndex >= questionIndex) {
              // The failure a builder produces by REORDERING: a question can only be shown or
              // hidden by an answer the patient has already had the chance to give. A forward
              // reference produces a form that cannot be completed, and nothing about the builder
              // screen shows it.
              errors.push(`${label} is shown based on "${refId}", which comes later on the form. A question can only depend on an answer given earlier.`);
            }
          }

          if (SHOW_IF_OPERATORS.indexOf(operator) === -1) {
            errors.push(`${label} uses an unknown display condition "${String(operator)}".`);
          }
        }
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * validateAnswers
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * A `date` answer the form can actually store.
 *
 * `Date.parse` is explicitly implementation-defined for anything that is not an ISO string, so
 * leaning on it would let node and a browser disagree — the exact class of divergence this pair of
 * files exists to rule out. So: an ISO `YYYY-MM-DD` prefix, and the day must be a real calendar day
 * (`2026-02-30` parses in some engines and is not a date).
 */
function isValidDateAnswer(value: any): boolean {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(value.trim());
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const asDate = new Date(Date.UTC(year, month - 1, day));
  return asDate.getUTCFullYear() === year
    && asDate.getUTCMonth() === month - 1
    && asDate.getUTCDate() === day;
}

/**
 * Validate a patient's answers against a schema.
 *
 * ONLY VISIBLE QUESTIONS ARE CONSIDERED — see the module header. Everything else here is a TAMPER
 * check as much as a usability one: the renderer cannot produce an answer outside a question's
 * options or outside its min/max, so a payload that carries one did not come from the form, and
 * the public submit endpoint has no session to fall back on.
 *
 * `statictext` is never validated — it carries no answer.
 *
 * At most ONE error per question, in document order: a field shows one message, and reporting
 * "required" and "not one of the options offered" for the same blank field is noise.
 */
export function validateAnswers(schema: FormSchema, answers: FormAnswers): AnswerValidationResult {
  const map: FormAnswers = isObject(answers) ? answers : {};
  const errors: AnswerError[] = [];

  for (const entry of visibleEntries(schema, answers)) {
    const question: any = entry.question;
    if (question.type === 'statictext') continue;

    const id: string = question.id;
    const answer = map[id];
    const present = isAnswered(answer);

    if (!present) {
      if (question.required === true) {
        errors.push({ questionId: id, message: 'This question must be answered.' });
      }
      continue; // an absent optional answer has nothing left to check
    }

    const optionValues: any[] = Array.isArray(question.options)
      ? question.options.filter((option: any) => isObject(option)).map((option: FormOption) => option.value)
      : [];

    if (question.type === 'checkbox') {
      if (!Array.isArray(answer)) {
        errors.push({ questionId: id, message: 'Choose one or more of the options listed.' });
        continue;
      }
      const bad = answer.find(
        (chosen: any) => !optionValues.some((value) => looseEq(value, chosen))
      );
      if (bad !== undefined) {
        errors.push({
          questionId: id,
          message: `"${String(bad)}" is not one of the options offered for this question.`,
        });
      }
      continue;
    }

    if (question.type === 'radio' || question.type === 'select') {
      if (Array.isArray(answer) || isObject(answer)) {
        errors.push({ questionId: id, message: 'Choose one of the options listed.' });
        continue;
      }
      if (!optionValues.some((value) => looseEq(value, answer))) {
        errors.push({
          questionId: id,
          message: `"${String(answer)}" is not one of the options offered for this question.`,
        });
      }
      continue;
    }

    if (question.type === 'number' || question.type === 'scale') {
      const numeric = toNumberOrNull(answer);
      if (numeric === null) {
        errors.push({ questionId: id, message: 'Enter a number.' });
        continue;
      }
      const min = toNumberOrNull(question.min);
      const max = toNumberOrNull(question.max);
      if (min !== null && numeric < min) {
        errors.push({ questionId: id, message: `Enter a number of ${min} or more.` });
        continue;
      }
      if (max !== null && numeric > max) {
        errors.push({ questionId: id, message: `Enter a number of ${max} or less.` });
      }
      continue;
    }

    if (question.type === 'date') {
      if (!isValidDateAnswer(answer)) {
        errors.push({ questionId: id, message: 'Enter a valid date.' });
      }
      continue;
    }

    if (question.type === 'yesno') {
      // The renderer emits a boolean; a JSON round-trip through a query string or a hand-built
      // payload can arrive as the strings 'true'/'false'. Anything else is not an answer this
      // question can hold.
      const ok = answer === true || answer === false
        || answer === 'true' || answer === 'false';
      if (!ok) {
        errors.push({ questionId: id, message: 'Answer yes or no.' });
      }
      continue;
    }

    // text | textarea | signature — free text. Structure is all there is to check.
    if (typeof answer !== 'string') {
      errors.push({
        questionId: id,
        message: 'This answer is not in a format the form can accept.',
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * scoreAnswers
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Does ANY option ANYWHERE in the schema carry a numeric score? Decides scored vs unscored. */
function schemaIsScored(schema: any): boolean {
  for (const entry of flatten(schema)) {
    const options: any = (entry.question as any).options;
    if (!Array.isArray(options)) continue;
    for (const option of options) {
      if (optionScore(option) !== null) return true;
    }
  }
  return false;
}

/**
 * Score a submission — §13.1's "Scoring" paragraph.
 *
 *   total  the `score` of every SELECTED option on every VISIBLE question
 *   max    per VISIBLE question that has any scored option, that question's highest single score
 *          — EXCEPT `checkbox`, where several can be chosen at once, so its ceiling is the sum of
 *          its POSITIVE scores
 *   bySection  { [sectionId]: { total, max } }
 *
 * `max` is a property of the FORM, not of the answers: an unanswered scored question still raises
 * the ceiling, which is what makes "4 out of 20" mean anything.
 *
 * ── NULL IS NOT ZERO ─────────────────────────────────────────────────────────────────────────
 *
 * A schema with no `score` on any option ANYWHERE returns `null`, not `{ total: 0, max: 0 }`.
 * "This form is not a scored instrument" and "this patient scored zero" are different clinical
 * facts, and `PatientFormRequests.scoreTotal` is nullable precisely so it can hold the difference.
 * A consent form reported as "0" would sort alongside the worst depression screen in the §13.2
 * score report.
 *
 * The test is on the SCHEMA, not on the answers: a scored questionnaire where the patient happened
 * to pick every zero-scoring option returns `{ total: 0, ... }`, not null.
 *
 * `bySection` carries an entry only for sections that contain at least one VISIBLE question with
 * scored options, so its entries always sum exactly to `total` and `max`. A section of free-text
 * questions does not appear as a misleading `0 / 0` row.
 */
export function scoreAnswers(schema: FormSchema, answers: FormAnswers): FormScore | null {
  if (!schemaIsScored(schema)) return null;

  const map: FormAnswers = isObject(answers) ? answers : {};
  let total = 0;
  let max = 0;
  const bySection: { [sectionId: string]: { total: number; max: number } } = {};

  for (const entry of visibleEntries(schema, answers)) {
    const question: any = entry.question;
    const options: any[] = Array.isArray(question.options)
      ? question.options.filter((option: any) => isObject(option))
      : [];
    const scored = options.filter((option) => optionScore(option) !== null);
    if (scored.length === 0) continue;

    const answer = map[question.id];
    const selected: any[] = Array.isArray(answer) ? answer : [answer];

    let questionTotal = 0;
    for (const option of scored) {
      // Iterating OPTIONS (not the answer array) means a duplicated value in a tampered checkbox
      // payload cannot be counted twice.
      if (selected.some((chosen) => looseEq(option.value, chosen))) {
        questionTotal += optionScore(option) as number;
      }
    }

    let questionMax: number;
    if (question.type === 'checkbox') {
      questionMax = scored.reduce((sum: number, option: any) => {
        const value = optionScore(option) as number;
        return value > 0 ? sum + value : sum;
      }, 0);
    } else {
      questionMax = scored.reduce((best: number | null, option: any) => {
        const value = optionScore(option) as number;
        return best === null || value > best ? value : best;
      }, null) as number;
    }

    total += questionTotal;
    max += questionMax;

    const sectionId: string = (entry.section as any).id;
    if (!Object.prototype.hasOwnProperty.call(bySection, sectionId)) {
      bySection[sectionId] = { total: 0, max: 0 };
    }
    bySection[sectionId].total += questionTotal;
    bySection[sectionId].max += questionMax;
  }

  return { total, max, bySection };
}
